import {
  ifToSwitchConfig,
  type ExpressionAst,
  type IfConfigValue,
  type JsonValue,
  type SwitchConfigValue,
} from './functional-control-flow';

/**
 * Client-side mirror of the backend's constrained expression evaluator
 * (`services/workflow_ir/expressions.py`). It exists only to *preview* what
 * a Switch or Validate would do with a sample value; the backend remains the
 * authority at run time. Strictness is copied on purpose:
 *
 * - a missing path is an error (`path_missing`), not a non-match;
 * - `eq`/`ne` require the same JSON type on both sides;
 * - `lt`/`lte`/`gt`/`gte` compare two numbers or two strings only;
 * - `in`/`not_in` need an array collection;
 * - `and`/`or`/`not` need boolean operands.
 */

export type ExpressionErrorCode =
  | 'path_missing'
  | 'path_type_mismatch'
  | 'comparison_type_mismatch'
  | 'membership_collection_required'
  | 'boolean_type_required'
  | 'expression_shape'
  // formula operators (Phase 8a), same codes as expressions.py
  | 'arithmetic_type_mismatch'
  | 'division_by_zero'
  | 'number_out_of_range'
  | 'concat_type_mismatch'
  | 'call_type_mismatch'
  | 'index_type_mismatch'
  | 'index_out_of_range'
  | 'key_missing'
  | 'result_too_large'
  | 'comprehension_too_deep';

export const MAX_RESULT_LIST_ITEMS = 256;
export const MAX_RESULT_TEXT_BYTES = 16_384;
export const MAX_COMPREHENSION_DEPTH = 2;

export class ExpressionEvaluationError extends Error {
  code: ExpressionErrorCode;
  /** The path involved, when the error is about a path. */
  path?: Array<string | number>;

  constructor(
    code: ExpressionErrorCode,
    message: string,
    path?: Array<string | number>,
  ) {
    super(message);
    this.name = 'ExpressionEvaluationError';
    this.code = code;
    this.path = path;
  }
}

type JsonType = 'null' | 'boolean' | 'number' | 'string' | 'array' | 'object';

export const jsonTypeOf = (value: unknown): JsonType => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  if (Array.isArray(value)) return 'array';
  return 'object';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const formatPath = (path: ReadonlyArray<string | number>): string =>
  path.length === 0 ? '(the whole value)' : path.join('.');

const resolvePath = (
  path: Array<string | number>,
  context: Record<string, unknown>,
): unknown => {
  let current: unknown = context;
  for (const segment of path) {
    if (typeof segment === 'string') {
      if (!isRecord(current)) {
        throw new ExpressionEvaluationError(
          'path_type_mismatch',
          `${formatPath(path)} is not an object, so its field "${segment}" cannot be read.`,
          path,
        );
      }
      if (!(segment in current)) {
        throw new ExpressionEvaluationError(
          'path_missing',
          `The field ${formatPath(path)} is missing.`,
          path,
        );
      }
      current = current[segment];
      continue;
    }
    if (!Array.isArray(current)) {
      throw new ExpressionEvaluationError(
        'path_type_mismatch',
        `${formatPath(path)} is not a list, so item ${segment} cannot be read.`,
        path,
      );
    }
    if (segment >= current.length) {
      throw new ExpressionEvaluationError(
        'path_missing',
        `The list at ${formatPath(path)} has no item ${segment}.`,
        path,
      );
    }
    current = current[segment];
  }
  return current;
};

const strictEqual = (left: unknown, right: unknown): boolean => {
  const leftType = jsonTypeOf(left);
  const rightType = jsonTypeOf(right);
  if (leftType !== rightType) {
    throw new ExpressionEvaluationError(
      'comparison_type_mismatch',
      `Cannot compare a ${leftType} with a ${rightType}.`,
    );
  }
  if (leftType === 'array') {
    const l = left as unknown[];
    const r = right as unknown[];
    return (
      l.length === r.length && l.every((item, i) => strictEqual(item, r[i]))
    );
  }
  if (leftType === 'object') {
    const l = left as Record<string, unknown>;
    const r = right as Record<string, unknown>;
    const lk = Object.keys(l);
    const rk = new Set(Object.keys(r));
    if (lk.length !== rk.size || !lk.every((key) => rk.has(key))) return false;
    return lk.every((key) => strictEqual(l[key], r[key]));
  }
  if (leftType === 'null') return true;
  return left === right;
};

const orderedCompare = (
  op: 'lt' | 'lte' | 'gt' | 'gte',
  left: unknown,
  right: unknown,
): boolean => {
  const leftType = jsonTypeOf(left);
  const rightType = jsonTypeOf(right);
  if (
    leftType !== rightType ||
    (leftType !== 'number' && leftType !== 'string')
  ) {
    throw new ExpressionEvaluationError(
      'comparison_type_mismatch',
      `Ordered comparisons need two numbers or two strings (got ${leftType} and ${rightType}).`,
    );
  }
  const l = left as number | string;
  const r = right as number | string;
  switch (op) {
    case 'lt':
      return l < r;
    case 'lte':
      return l <= r;
    case 'gt':
      return l > r;
    default:
      return l >= r;
  }
};

const requireBoolean = (value: unknown, what: string): boolean => {
  if (typeof value !== 'boolean') {
    throw new ExpressionEvaluationError(
      'boolean_type_required',
      `${what} needs a true/false condition, but got a ${jsonTypeOf(value)}.`,
    );
  }
  return value;
};

export const evaluateExpression = (
  expression: ExpressionAst,
  context: Record<string, unknown>,
): JsonValue => {
  let comprehensionDepth = 0;
  const evaluate = (
    node: ExpressionAst,
    scope: Record<string, unknown> = context,
  ): unknown => {
    const sub = (child: ExpressionAst): unknown => evaluate(child, scope);
    if (!isRecord(node) || typeof node.op !== 'string') {
      throw new ExpressionEvaluationError(
        'expression_shape',
        'The condition is not a valid expression.',
      );
    }
    switch (node.op) {
      case 'literal':
        return node.value;
      case 'path':
        return resolvePath(node.path, scope);
      case 'exists':
        try {
          resolvePath(node.path.path, scope);
          return true;
        } catch (error) {
          if (
            error instanceof ExpressionEvaluationError &&
            (error.code === 'path_missing' ||
              error.code === 'path_type_mismatch')
          ) {
            return false;
          }
          throw error;
        }
      case 'is_null': {
        const value = sub(node.arg);
        return value === null || value === undefined;
      }
      case 'not':
        return !requireBoolean(sub(node.arg), '"not"');
      case 'and': {
        for (const arg of node.args) {
          if (!requireBoolean(sub(arg), '"all of"')) return false;
        }
        return true;
      }
      case 'or': {
        for (const arg of node.args) {
          if (requireBoolean(sub(arg), '"any of"')) return true;
        }
        return false;
      }
      case 'eq':
        return strictEqual(sub(node.left), sub(node.right));
      case 'ne':
        return !strictEqual(sub(node.left), sub(node.right));
      case 'lt':
      case 'lte':
      case 'gt':
      case 'gte':
        return orderedCompare(node.op, sub(node.left), sub(node.right));
      case 'in':
      case 'not_in': {
        const item = sub(node.item);
        const collection = sub(node.collection);
        if (!Array.isArray(collection)) {
          throw new ExpressionEvaluationError(
            'membership_collection_required',
            '"is one of" needs a list of values to check against.',
          );
        }
        const member = collection.some((candidate) =>
          strictEqual(item, candidate),
        );
        return node.op === 'in' ? member : !member;
      }
      // --- formula operators (Phase 8a) ---
      case 'add':
      case 'sub':
      case 'mul':
      case 'div':
      case 'mod':
      case 'pow':
        return arithmetic(node.op, sub(node.left), sub(node.right));
      case 'neg':
        return checkNumber(-requireNumber(sub(node.arg), 'neg', 'arg'), 'neg');
      case 'concat':
        return boundedText(
          node.args.map((arg, i) => toText(sub(arg), 'concat', i)).join(''),
          'concat',
        );
      case 'index':
        return indexInto(sub(node.base), sub(node.index));
      case 'object': {
        const out: Record<string, unknown> = {};
        for (const [key, child] of Object.entries(node.fields)) {
          out[key] = sub(child);
        }
        return out;
      }
      case 'list':
        return node.items.map((item) => sub(item));
      case 'map':
      case 'filter':
      case 'find':
      case 'any':
      case 'all': {
        if (comprehensionDepth >= MAX_COMPREHENSION_DEPTH) {
          throw new ExpressionEvaluationError(
            'comprehension_too_deep',
            `map/filter/find/any/all may nest at most ${MAX_COMPREHENSION_DEPTH} levels.`,
          );
        }
        const source = sub(node.over);
        if (!Array.isArray(source)) {
          throw new ExpressionEvaluationError(
            'call_type_mismatch',
            `${node.op}() needs a list, but got ${article(jsonTypeOf(source))}.`,
          );
        }
        if (source.length > MAX_RESULT_LIST_ITEMS) {
          throw new ExpressionEvaluationError(
            'result_too_large',
            `${node.op}() may look at ${MAX_RESULT_LIST_ITEMS} items at most.`,
          );
        }
        comprehensionDepth += 1;
        try {
          const body = (item: unknown, i: number): unknown =>
            evaluate(node.body, { ...scope, it: item, index: i });
          const truthy = (item: unknown, i: number): boolean =>
            requireBoolean(
              body(item, i),
              `the condition given to ${node.op}()`,
            );
          switch (node.op) {
            case 'map':
              return source.map(body);
            case 'filter':
              return source.filter(truthy);
            case 'find': {
              const found = source.findIndex(truthy);
              return found === -1 ? null : source[found];
            }
            case 'any':
              return source.some(truthy);
            default:
              return source.every(truthy);
          }
        } finally {
          comprehensionDepth -= 1;
        }
      }
      case 'call':
        return callFunction(node.fn, node.args, sub);
      default:
        throw new ExpressionEvaluationError(
          'expression_shape',
          `Unknown operator "${(node as { op: string }).op}".`,
        );
    }
  };
  return evaluate(expression) as JsonValue;
};

// --- formula operator helpers (Phase 8a; mirror expressions.py) -------------

const article = (type: JsonType): string =>
  type === 'null'
    ? 'null'
    : type === 'boolean'
      ? 'true/false'
      : type === 'number'
        ? 'a number'
        : type === 'string'
          ? 'text'
          : type === 'array'
            ? 'a list'
            : 'an object';

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

const requireNumber = (value: unknown, op: string, side: string): number => {
  if (isNumber(value)) return value;
  throw new ExpressionEvaluationError(
    'arithmetic_type_mismatch',
    `'${OPERATOR_SYMBOLS[op] ?? op}' needs numbers, but the ${side} side is ${article(jsonTypeOf(value))}.`,
  );
};

const checkNumber = (value: number, op: string): number => {
  if (!Number.isFinite(value) || Math.abs(value) > 1e308) {
    throw new ExpressionEvaluationError(
      'number_out_of_range',
      `'${OPERATOR_SYMBOLS[op] ?? op}' produced a number that is too large or not finite.`,
    );
  }
  return value;
};

const OPERATOR_SYMBOLS: Record<string, string> = {
  add: '+',
  sub: '-',
  mul: '*',
  div: '/',
  mod: '%',
  pow: '^',
  neg: '-',
};

/** Python-style modulo: the result takes the sign of the divisor. */
const pyMod = (a: number, b: number): number => ((a % b) + b) % b;

const arithmetic = (op: string, left: unknown, right: unknown): number => {
  const l = requireNumber(left, op, 'left');
  const r = requireNumber(right, op, 'right');
  switch (op) {
    case 'add':
      return checkNumber(l + r, op);
    case 'sub':
      return checkNumber(l - r, op);
    case 'mul':
      return checkNumber(l * r, op);
    case 'div':
      if (r === 0) {
        throw new ExpressionEvaluationError(
          'division_by_zero',
          'Divides by zero.',
        );
      }
      return checkNumber(l / r, op);
    case 'mod':
      if (r === 0) {
        throw new ExpressionEvaluationError(
          'division_by_zero',
          'Takes a remainder by zero.',
        );
      }
      return checkNumber(pyMod(l, r), op);
    default: {
      if (l < 0 && !Number.isInteger(r)) {
        throw new ExpressionEvaluationError(
          'number_out_of_range',
          'A negative number cannot be raised to a fractional power.',
        );
      }
      return checkNumber(Math.pow(l, r), op);
    }
  }
};

const toText = (value: unknown, op: string, position: number): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (isNumber(value)) return formatNumber(value);
  throw new ExpressionEvaluationError(
    op === 'concat' ? 'concat_type_mismatch' : 'call_type_mismatch',
    op === 'concat'
      ? `'~' joins text, numbers and true/false, but part ${position + 1} is ${article(jsonTypeOf(value))}.`
      : `${op}() cannot turn ${article(jsonTypeOf(value))} into text.`,
  );
};

/** Numbers print the way Python's json/repr would (1.0 → "1", 2.5 → "2.5"). */
export const formatNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(value);

const boundedText = (text: string, op: string): string => {
  if (new TextEncoder().encode(text).length > MAX_RESULT_TEXT_BYTES) {
    throw new ExpressionEvaluationError(
      'result_too_large',
      `The text produced by '${OPERATOR_SYMBOLS[op] ?? op}' is longer than ${MAX_RESULT_TEXT_BYTES} bytes.`,
    );
  }
  return text;
};

const boundedList = <T>(items: T[], op: string): T[] => {
  if (items.length > MAX_RESULT_LIST_ITEMS) {
    throw new ExpressionEvaluationError(
      'result_too_large',
      `The list produced by ${op}() is longer than ${MAX_RESULT_LIST_ITEMS} items.`,
    );
  }
  return items;
};

const indexInto = (base: unknown, key: unknown): unknown => {
  if (Array.isArray(base)) {
    if (!isNumber(key) || !Number.isInteger(key)) {
      throw new ExpressionEvaluationError(
        'index_type_mismatch',
        'A list position must be a whole number.',
      );
    }
    if (key < -base.length || key >= base.length) {
      throw new ExpressionEvaluationError(
        'index_out_of_range',
        `Reads a list position that does not exist (it has ${base.length} items).`,
      );
    }
    return base[key < 0 ? base.length + key : key];
  }
  if (isRecord(base)) {
    if (typeof key !== 'string') {
      throw new ExpressionEvaluationError(
        'index_type_mismatch',
        'An object key must be text.',
      );
    }
    if (!(key in base)) {
      throw new ExpressionEvaluationError(
        'key_missing',
        `Reads the key '${key}', which the object does not have.`,
      );
    }
    return base[key];
  }
  throw new ExpressionEvaluationError(
    'index_type_mismatch',
    `Only lists and objects can be indexed, not ${article(jsonTypeOf(base))}.`,
  );
};

const looseEqual = (left: unknown, right: unknown): boolean => {
  try {
    return strictEqual(left, right);
  } catch (error) {
    if (
      error instanceof ExpressionEvaluationError &&
      error.code === 'comparison_type_mismatch'
    ) {
      return false;
    }
    throw error;
  }
};

const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, inner) =>
    isRecord(inner)
      ? Object.fromEntries(
          Object.keys(inner)
            .sort()
            .map((key) => [key, inner[key]]),
        )
      : inner,
  );

const needText = (value: unknown, fn: string, position: number): string => {
  if (typeof value === 'string') return value;
  throw new ExpressionEvaluationError(
    'call_type_mismatch',
    `${fn}() got ${article(jsonTypeOf(value))} as ${position > 0 ? `argument ${position + 1}` : 'its argument'} (it needs text).`,
  );
};

const needList = (value: unknown, fn: string, position = 0): unknown[] => {
  if (Array.isArray(value)) return value;
  throw new ExpressionEvaluationError(
    'call_type_mismatch',
    `${fn}() got ${article(jsonTypeOf(value))} as ${position > 0 ? `argument ${position + 1}` : 'its argument'} (it needs a list).`,
  );
};

const needInt = (value: unknown, fn: string, position: number): number => {
  if (isNumber(value) && Number.isInteger(value)) return value;
  throw new ExpressionEvaluationError(
    'call_type_mismatch',
    `${fn}() got ${article(jsonTypeOf(value))} as ${position > 0 ? `argument ${position + 1}` : 'its argument'} (it needs a whole number).`,
  );
};

const needNumber = (value: unknown, fn: string, position: number): number => {
  if (isNumber(value)) return value;
  throw new ExpressionEvaluationError(
    'arithmetic_type_mismatch',
    `${fn}() needs numbers, but ${position > 0 ? `argument ${position + 1}` : 'one of its values'} is ${article(jsonTypeOf(value))}.`,
  );
};

const sortKey = (value: unknown): [number, number | string] => {
  const type = jsonTypeOf(value);
  const order = { null: 0, boolean: 1, number: 2, string: 3 }[
    type as 'null' | 'boolean' | 'number' | 'string'
  ];
  if (order === undefined) {
    throw new ExpressionEvaluationError(
      'call_type_mismatch',
      'sort() needs a list of numbers or text.',
    );
  }
  if (type === 'null') return [0, 0];
  if (type === 'boolean') return [1, value ? 1 : 0];
  return [order, value as number | string];
};

const callFunction = (
  fn: string,
  argNodes: ExpressionAst[],
  sub: (node: ExpressionAst) => unknown,
): unknown => {
  if (fn === 'if') {
    const condition = requireBoolean(
      sub(argNodes[0]),
      'the condition given to if()',
    );
    return sub(condition ? argNodes[1] : argNodes[2]);
  }
  if (fn === 'coalesce') {
    for (const arg of argNodes) {
      const value = sub(arg);
      if (value !== null && value !== undefined) return value;
    }
    return null;
  }
  const args = argNodes.map(sub);
  switch (fn) {
    case 'abs':
      return checkNumber(Math.abs(needNumber(args[0], fn, 0)), fn);
    case 'round': {
      const n = needNumber(args[0], fn, 0);
      const digits = args.length > 1 ? needInt(args[1], fn, 1) : 0;
      if (digits === 0) return checkNumber(roundHalfEven(n), fn);
      const factor = Math.pow(10, digits);
      return checkNumber(Math.round(n * factor) / factor, fn);
    }
    case 'floor':
      return checkNumber(Math.floor(needNumber(args[0], fn, 0)), fn);
    case 'ceil':
      return checkNumber(Math.ceil(needNumber(args[0], fn, 0)), fn);
    case 'sqrt': {
      const n = needNumber(args[0], fn, 0);
      if (n < 0) {
        throw new ExpressionEvaluationError(
          'number_out_of_range',
          'sqrt() needs a non-negative number.',
        );
      }
      return Math.sqrt(n);
    }
    case 'min':
    case 'max': {
      const values =
        args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
      if (values.length === 0) {
        throw new ExpressionEvaluationError(
          'call_type_mismatch',
          `${fn}() of an empty list has no value.`,
        );
      }
      const numbers = values.map((value, i) => needNumber(value, fn, i));
      return fn === 'min' ? Math.min(...numbers) : Math.max(...numbers);
    }
    case 'sum':
      return checkNumber(
        needList(args[0], fn).reduce<number>(
          (acc, v, i) => acc + needNumber(v, fn, i),
          0,
        ),
        fn,
      );
    case 'avg': {
      const items = needList(args[0], fn);
      if (items.length === 0) {
        throw new ExpressionEvaluationError(
          'division_by_zero',
          'avg() of an empty list has no value.',
        );
      }
      return checkNumber(
        items.reduce<number>((acc, v, i) => acc + needNumber(v, fn, i), 0) /
          items.length,
        fn,
      );
    }
    case 'len': {
      const value = args[0];
      if (typeof value === 'string') return [...value].length;
      if (Array.isArray(value)) return value.length;
      if (isRecord(value)) return Object.keys(value).length;
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `len() needs text, a list or an object, not ${article(jsonTypeOf(value))}.`,
      );
    }
    case 'lower':
      return needText(args[0], fn, 0).toLowerCase();
    case 'upper':
      return needText(args[0], fn, 0).toUpperCase();
    case 'trim':
      return needText(args[0], fn, 0).trim();
    case 'split': {
      const text = needText(args[0], fn, 0);
      const separator = needText(args[1], fn, 1);
      return boundedList(separator ? text.split(separator) : [...text], fn);
    }
    case 'join': {
      const items = needList(args[0], fn);
      const separator = needText(args[1], fn, 1);
      return boundedText(
        items.map((item, i) => toText(item, fn, i)).join(separator),
        fn,
      );
    }
    case 'slice': {
      const value = args[0];
      const start = needInt(args[1], fn, 1);
      const stop = args.length > 2 ? needInt(args[2], fn, 2) : undefined;
      if (typeof value === 'string' || Array.isArray(value)) {
        return value.slice(start, stop);
      }
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `slice() needs text or a list, not ${article(jsonTypeOf(value))}.`,
      );
    }
    case 'contains': {
      const haystack = args[0];
      if (typeof haystack === 'string')
        return haystack.includes(needText(args[1], fn, 1));
      if (Array.isArray(haystack))
        return haystack.some((item) => looseEqual(args[1], item));
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `contains() needs text or a list, not ${article(jsonTypeOf(haystack))}.`,
      );
    }
    case 'starts_with':
      return needText(args[0], fn, 0).startsWith(needText(args[1], fn, 1));
    case 'ends_with':
      return needText(args[0], fn, 0).endsWith(needText(args[1], fn, 1));
    case 'replace': {
      const text = needText(args[0], fn, 0);
      const from = needText(args[1], fn, 1);
      const to = needText(args[2], fn, 2);
      return boundedText(from ? text.split(from).join(to) : text, fn);
    }
    case 'reverse': {
      const value = args[0];
      if (typeof value === 'string') return [...value].reverse().join('');
      if (Array.isArray(value)) return [...value].reverse();
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `reverse() needs text or a list, not ${article(jsonTypeOf(value))}.`,
      );
    }
    case 'to_string': {
      const value = args[0];
      if (
        value === null ||
        value === undefined ||
        Array.isArray(value) ||
        isRecord(value)
      ) {
        return boundedText(canonical(value ?? null), fn);
      }
      return toText(value, fn, 0);
    }
    case 'to_number': {
      const value = args[0];
      if (isNumber(value)) return value;
      if (typeof value === 'boolean') return value ? 1 : 0;
      if (typeof value === 'string') {
        const text = value.trim();
        const parsed = /^[+-]?\d+$/.test(text)
          ? parseInt(text, 10)
          : Number(text);
        if (text !== '' && Number.isFinite(parsed)) return parsed;
      }
      if (args.length > 1) return args[1];
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        'to_number() could not read the value as a number.',
      );
    }
    case 'to_bool': {
      const value = args[0];
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const lowered = value.trim().toLowerCase();
        if (['true', 'yes', '1'].includes(lowered)) return true;
        if (['false', 'no', '0', ''].includes(lowered)) return false;
      }
      if (isNumber(value)) return value !== 0;
      if (value === null || value === undefined) return false;
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        'to_bool() needs true/false, text, a number or null.',
      );
    }
    case 'type_of':
      return jsonTypeOf(args[0]);
    case 'is_number':
      return isNumber(args[0]);
    case 'is_string':
      return typeof args[0] === 'string';
    case 'first':
    case 'last': {
      const items = needList(args[0], fn);
      if (items.length === 0) return null;
      return fn === 'first' ? items[0] : items[items.length - 1];
    }
    case 'append':
      return boundedList([...needList(args[0], fn), args[1]], fn);
    case 'set_at': {
      const items = [...needList(args[0], fn)];
      const position = needInt(args[1], fn, 1);
      if (position < -items.length || position >= items.length) {
        throw new ExpressionEvaluationError(
          'index_out_of_range',
          `set_at() position is out of range (the list has ${items.length} items).`,
        );
      }
      items[position < 0 ? items.length + position : position] = args[2];
      return items;
    }
    case 'range': {
      const bounds = args.map((arg, i) => needInt(arg, fn, i));
      const [start, stop, step] =
        bounds.length === 1
          ? [0, bounds[0], 1]
          : bounds.length === 2
            ? [bounds[0], bounds[1], 1]
            : [bounds[0], bounds[1], bounds[2]];
      if (step === 0) {
        throw new ExpressionEvaluationError(
          'division_by_zero',
          'range() cannot step by 0.',
        );
      }
      const count = Math.max(0, Math.ceil((stop - start) / step));
      if (count > MAX_RESULT_LIST_ITEMS) {
        throw new ExpressionEvaluationError(
          'result_too_large',
          `range() would produce more than ${MAX_RESULT_LIST_ITEMS} items.`,
        );
      }
      return Array.from({ length: count }, (_, i) => start + i * step);
    }
    case 'keys':
    case 'values': {
      const value = args[0];
      if (!isRecord(value)) {
        throw new ExpressionEvaluationError(
          'call_type_mismatch',
          `${fn}() needs an object, not ${article(jsonTypeOf(value))}.`,
        );
      }
      return boundedList(
        fn === 'keys' ? Object.keys(value) : Object.values(value),
        fn,
      );
    }
    case 'has': {
      const value = args[0];
      if (isRecord(value))
        return typeof args[1] === 'string' && args[1] in value;
      if (Array.isArray(value))
        return value.some((item) => looseEqual(args[1], item));
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `has() needs an object or a list, not ${article(jsonTypeOf(value))}.`,
      );
    }
    case 'get':
    case 'at':
      try {
        return indexInto(args[0], args[1]);
      } catch (error) {
        if (
          args.length > 2 &&
          error instanceof ExpressionEvaluationError &&
          (error.code === 'key_missing' ||
            error.code === 'index_out_of_range' ||
            (error.code === 'index_type_mismatch' && args[0] === null))
        ) {
          return args[2];
        }
        throw error;
      }
    case 'index_of': {
      const items = args[0];
      if (typeof items === 'string')
        return items.indexOf(needText(args[1], fn, 1));
      if (Array.isArray(items))
        return items.findIndex((item) => looseEqual(args[1], item));
      throw new ExpressionEvaluationError(
        'call_type_mismatch',
        `index_of() needs text or a list, not ${article(jsonTypeOf(items))}.`,
      );
    }
    case 'sort':
      return [...needList(args[0], fn)].sort((a, b) => {
        const [ka, va] = sortKey(a);
        const [kb, vb] = sortKey(b);
        if (ka !== kb) return ka - kb;
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    case 'distinct': {
      const seen = new Set<string>();
      const unique: unknown[] = [];
      for (const item of needList(args[0], fn)) {
        const key = canonical(item);
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(item);
        }
      }
      return unique;
    }
    case 'flatten':
      return boundedList(
        needList(args[0], fn).flatMap((item) =>
          Array.isArray(item) ? item : [item],
        ),
        fn,
      );
    default:
      throw new ExpressionEvaluationError(
        'expression_shape',
        `Unknown function "${fn}".`,
      );
  }
};

/** Python's round(): half to even at zero digits. */
const roundHalfEven = (n: number): number => {
  const floor = Math.floor(n);
  const diff = n - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
};

export type SwitchPreview =
  | { kind: 'unknown' }
  | { kind: 'case'; index: number; label: string }
  | { kind: 'default'; label: string }
  | {
      kind: 'fail';
      code: string;
      message: string;
      caseIndex?: number;
    };

/**
 * What the backend's `select_switch_case` would do with `value`
 * (`undefined` = no sample available).
 */
export const previewSwitchSelection = (
  config: SwitchConfigValue,
  value: JsonValue | undefined,
): SwitchPreview => {
  if (value === undefined) return { kind: 'unknown' };
  if (value === null) {
    if (config.nullBehavior === 'default' && config.defaultLabel) {
      return { kind: 'default', label: config.defaultLabel };
    }
    return {
      kind: 'fail',
      code: 'switch_null',
      message:
        'The incoming value is null and "If nothing matches" is set to fail the run.',
    };
  }
  for (const [index, switchCase] of config.cases.entries()) {
    let matches: unknown;
    try {
      matches = evaluateExpression(switchCase.when, { value });
    } catch (error) {
      const detail =
        error instanceof ExpressionEvaluationError
          ? error.message
          : 'the condition could not be evaluated.';
      return {
        kind: 'fail',
        code:
          error instanceof ExpressionEvaluationError
            ? error.code
            : 'switch_expression_error',
        caseIndex: index,
        message: `Case ${index + 1} ("${switchCase.label}") would fail the run: ${detail}`,
      };
    }
    if (typeof matches !== 'boolean') {
      return {
        kind: 'fail',
        code: 'switch_non_boolean',
        caseIndex: index,
        message: `Case ${index + 1} ("${switchCase.label}") does not produce true or false.`,
      };
    }
    if (matches) return { kind: 'case', index, label: switchCase.label };
  }
  if (config.defaultLabel) {
    return { kind: 'default', label: config.defaultLabel };
  }
  return {
    kind: 'fail',
    code: 'switch_no_match',
    message:
      'No case matches and there is no default branch, so the run would fail.',
  };
};

// --- if/else (Phase 8b) -----------------------------------------------------

export type IfPreview =
  | { kind: 'unknown' }
  | { kind: 'then' | 'else'; because: string }
  | { kind: 'fail'; code: string; message: string };

const MISSING_FIELD_CODES = new Set([
  'path_missing',
  'path_type_mismatch',
  'comparison_type_mismatch',
]);

/**
 * What the If node would do with `value`: the Switch preview over the If's
 * one-case form, with the friendly fallback applied (a missing field, a null
 * value or an incomparable pair take Otherwise unless `missingBehavior` is
 * `error`). `because` is a short clause for the sentence in the panel.
 */
export const previewIfSelection = (
  config: IfConfigValue,
  value: JsonValue | undefined,
  describeCondition: () => string,
): IfPreview => {
  if (value === undefined || !config.condition) return { kind: 'unknown' };
  const preview = previewSwitchSelection(ifToSwitchConfig(config), value);
  if (preview.kind === 'unknown') return preview;
  if (preview.kind === 'case') {
    return { kind: 'then', because: `${describeCondition()} holds` };
  }
  if (preview.kind === 'default') {
    return {
      kind: 'else',
      because:
        value === null
          ? 'the value is null'
          : `${describeCondition()} does not hold`,
    };
  }
  if (
    config.missingBehavior === 'else' &&
    MISSING_FIELD_CODES.has(preview.code)
  ) {
    // The client evaluator's messages name the field as `The field value.x is
    // missing.`; show it without the `value.` root, as the builder does.
    const field =
      /The (?:field|list at) (?:value\.)?([^\s]+?) (?:is missing|has no item)/.exec(
        preview.message,
      );
    const named = field?.[1];
    return {
      kind: 'else',
      because:
        preview.code === 'comparison_type_mismatch'
          ? named
            ? `${named} could not be compared`
            : 'the values could not be compared'
          : named
            ? `${named} is missing`
            : 'the field is missing',
    };
  }
  return {
    kind: 'fail',
    code: preview.code,
    message: preview.message.replace(/^Case 1 \("[^"]*"\) /, 'The condition '),
  };
};
// --- end if/else ------------------------------------------------------------
